import React from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

type InteriorHeroConfig = {
  slot: string;
  displayName: string;
  prefix: string;
  titleDefault: string;
  altDefault: string;
};

const TITLE_CLASS =
  'font-heading text-3xl sm:text-4xl md:text-5xl font-normal mb-3 uppercase tracking-wider';
const SUBTITLE_CLASS =
  'font-body font-light text-base sm:text-xl md:text-2xl leading-relaxed opacity-90 drop-shadow-md max-w-3xl';

export function createInteriorHero(config: InteriorHeroConfig) {
  const { slot, displayName, prefix, titleDefault, altDefault } = config;
  const titlePath = `${prefix}.title`;
  const subtitlePath = `${prefix}.subtitle`;
  const bgPath = `${prefix}.bg_image`;

  const runtimeDef: SlotGroupDefinition = {
    label: 'Background Image',
    fields: {
      image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
      alt: { label: 'Alt Text', type: 'text', defaultValue: altDefault },
    },
  };

  function InteriorHero() {
    const bgImage = useSlotGroup(bgPath, runtimeDef);

    return (
      <section className="relative h-[42vh] min-h-[300px] max-h-[420px] overflow-hidden">
        <EditableSlotGroup groupPath={bgPath} definition={runtimeDef} className="absolute inset-0">
          {bgImage.image ? (
            <VibesitesImage url={bgImage.image} alt={bgImage.alt || altDefault} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
          <div className="absolute inset-0 bg-black/40" />
        </EditableSlotGroup>

        <div
          className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-4"
          style={{ paddingTop: 'calc(var(--offer-banner-height, 0px) + 72px)' }}
        >
          <RichTextContent path={titlePath} as="h1" className={TITLE_CLASS} />
          <RichTextContent
            path={subtitlePath}
            label="Subtitle"
            defaultValue=""
            as="p"
            className={SUBTITLE_CLASS}
          />
        </div>
      </section>
    );
  }

  const contentSchema: ComponentContentSchema = {
    slot,
    displayName,
    tokens: {
      [titlePath]: {
        label: 'Title',
        type: 'richtext',
        defaultValue: `<p>${titleDefault}</p>`,
      },
      [subtitlePath]: {
        label: 'Subtitle',
        type: 'richtext',
        defaultValue: '',
      },
    },
    slotGroups: {
      [bgPath]: {
        label: 'Background Image',
        fields: {
          image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
          alt: { label: 'Alt Text', type: 'text', defaultValue: altDefault },
        },
      },
    },
  };

  (InteriorHero as any).contentSchema = contentSchema;
  InteriorHero.displayName = slot;

  return InteriorHero;
}
